/**
 * EEZ Layer
 * Exclusive Economic Zone boundaries layer
 * Uses ensureGeoJsonLayer for deterministic, idempotent layer management.
 *
 * Architecture: Two layers sharing one source.
 *
 *   1. SOLID layer  — all line types that should render as solid lines.
 *      Uses a simple `line-color` match expression and static `line-width`.
 *      NO `line-dasharray` at all, which avoids the Mapbox GL JS rendering bug
 *      where a data-driven `line-dasharray` (match expression) combined with
 *      zoom-interpolated `line-width` causes the SDF dash atlas to produce
 *      invisible output for features that fall through to the [1, 0] default.
 *
 *   2. DASHED layer — only the 5 "unsettled" / "archipelagic" line types.
 *      Filtered via a data-driven `filter`, with a STATIC `line-dasharray`
 *      (no match expression). Because every feature in this layer gets the
 *      same dash pattern, the SDF atlas works correctly.
 *
 * Why this fixes the bug:
 *   The original single-layer approach used a data-driven `line-dasharray`
 *   with a `match` expression whose default was [1, 0] (solid). Mapbox
 *   generates per-tile SDF dash atlases for data-driven dash patterns.
 *   The [1, 0] degenerate pattern (zero-length gap) produced an SDF texture
 *   that rendered as invisible on initial load. The LayersPanel's
 *   `backdrop-blur-xl` CSS forced the GPU compositor to re-sample the
 *   WebGL canvas, which — through compositor-level texture re-reads —
 *   caused the lines to appear. Splitting into two layers with only static
 *   dash patterns eliminates the broken SDF atlas entirely.
 */

import mapboxgl from 'mapbox-gl';
import type { MapPopupField, MapPopupProps } from '@/components/molecules/MapPopup';
import { setMapPopupContent } from '@/utils/map/mapPopup';
import { ensureGeoJsonLayer, waitForStyleLoaded } from '../geoJsonLayerHelper';

const LAYER_ID = 'EEZ Layer';
const DASHED_LAYER_ID = 'EEZ Dashed Layer';
const SOURCE_ID = 'EEZ Source';

/** Line types that should render with dashes */
const DASHED_LINE_TYPES = [
  'Archipelagic baseline',
  'Unsettled (maritime)',
  'Unsettled (land)',
  'Unsettled median line (maritime)',
  'Unsettled median line (land)',
];

/** Shared color expression — used by both layers */
const LINE_COLOR_EXPR = [
  'match',
  ['get', 'LINE_TYPE'],
  'Archipelagic baseline',
  '#FFB6C1',
  'Normal baseline (official)',
  '#FFCC99',
  'Straight baseline',
  '#FFFACD',
  '12 NM',
  '#E6E6FA',
  '200 NM',
  '#D8BFD8',
  'Median line',
  '#FFDAB9',
  'Treaty',
  '#FFE4E1',
  'Court ruling',
  '#F0E68C',
  'Joint regime',
  '#E0FFFF',
  'Unilateral claim (undisputed)',
  '#F5DEB3',
  'Unsettled (maritime)',
  '#AFEEEE',
  'Unsettled (land)',
  '#FFEFD5',
  'Unsettled median line (maritime)',
  '#D3D3D3',
  'Unsettled median line (land)',
  '#B0E0E6',
  'Connection line',
  '#FFF0F5',
  '#FFFFFF',
];

const LINE_WIDTH_EXPR = ['interpolate', ['linear'], ['zoom'], 0, 1, 10, 5];

/**
 * SOLID layer config — all line types EXCEPT the dashed ones.
 * No `line-dasharray` at all. Simple, reliable rendering.
 */
const EEZ_SOLID_CONFIG = {
  type: 'line' as const,
  filter: ['!', ['in', ['get', 'LINE_TYPE'], ['literal', DASHED_LINE_TYPES]]],
  paint: {
    'line-color': LINE_COLOR_EXPR,
    'line-width': LINE_WIDTH_EXPR,
  } as mapboxgl.LinePaint,
  layout: { visibility: 'none' as const },
};

/**
 * DASHED layer config — only the 5 dashed line types.
 * Static `line-dasharray` (same for every feature in this layer).
 */
const EEZ_DASHED_CONFIG = {
  type: 'line' as const,
  filter: ['in', ['get', 'LINE_TYPE'], ['literal', DASHED_LINE_TYPES]],
  paint: {
    'line-color': LINE_COLOR_EXPR,
    'line-width': LINE_WIDTH_EXPR,
    'line-dasharray': [4, 2] as number[],
  } as unknown as mapboxgl.LinePaint,
  layout: { visibility: 'none' as const },
};

/**
 * Add EEZ layer
 * @param map - Mapbox map instance
 */
export async function addEEZ(map: mapboxgl.Map | null | undefined): Promise<void> {
  if (!map) return;

  try {
    // Wait for style to be loaded
    const styleReady = await waitForStyleLoaded(map);
    if (!styleReady) {
      // Throw (not return) so layerRegistry does NOT mark 'eez' initialized,
      // leaving it retryable on the next toggle / re-init.
      throw new Error('EEZ: style not ready (initial wait)');
    }

    // Fetch GeoJSON data
    const response = await fetch('/data/eez_boundaries.geojson');
    if (!response.ok) {
      throw new Error(`Failed to fetch eez_boundaries.geojson: ${response.status}`);
    }
    const eezData: GeoJSON.FeatureCollection = await response.json();

    // The 36MB fetch+parse above can outlast a transient "style loaded" window on the
    // globe (satellite tiles keep streaming), which would make ensureGeoJsonLayer's
    // isStyleLoaded() gate fail and silently skip the add. Re-wait for style readiness
    // immediately before adding so the gate passes.
    const styleReadyForAdd = await waitForStyleLoaded(map);
    if (!styleReadyForAdd) {
      throw new Error('EEZ: style not ready when adding layer');
    }

    // Add the solid layer (majority of features)
    const addedSolid = ensureGeoJsonLayer({
      map,
      sourceId: SOURCE_ID,
      layerId: LAYER_ID,
      data: eezData,
      layerConfig: EEZ_SOLID_CONFIG,
    });

    if (!addedSolid) {
      // Throw (not return) so the registry leaves 'eez' un-initialized and a retry can run.
      throw new Error('EEZ: ensureGeoJsonLayer returned false for solid layer');
    }

    // Add the dashed layer on top (shares the same source)
    // We add the layer manually because ensureGeoJsonLayer would try to
    // create a new source, but the source already exists.
    if (!map.getLayer(DASHED_LAYER_ID)) {
      try {
        map.addLayer({
          id: DASHED_LAYER_ID,
          type: 'line',
          source: SOURCE_ID,
          filter: EEZ_DASHED_CONFIG.filter as mapboxgl.Expression,
          paint: EEZ_DASHED_CONFIG.paint,
          layout: EEZ_DASHED_CONFIG.layout,
        });
      } catch (err) {
        console.error('EEZ: failed to add dashed layer', err);
      }
    }

    // Trigger a repaint to ensure both layers are rendered immediately
    map.triggerRepaint();

    // Set up hover popup for both layers
    const popup = new mapboxgl.Popup({ closeButton: false, offset: 16, maxWidth: '360px' });

    const handleMouseEnter = (e: mapboxgl.MapLayerMouseEvent) => {
      map.getCanvas().style.cursor = 'pointer';
      const properties = e.features?.[0]?.properties;
      if (!properties) return;

      const sources = [properties.SOURCE1, properties.SOURCE2].filter(Boolean) as string[];
      const fields: MapPopupField[] = [];
      if (properties.EEZ1) {
        fields.push({ label: 'EEZ 1', value: properties.EEZ1 });
      }
      if (properties.EEZ2) {
        fields.push({ label: 'EEZ 2', value: properties.EEZ2 });
      }
      if (sources.length) {
        fields.push({ label: 'Sources', value: sources.join(', '), valueClassName: 'text-xs' });
      }

      const popupProps: MapPopupProps = {
        badge: { label: 'EEZ Boundary', variant: 'info' },
        title: properties.LINE_TYPE || 'Exclusive Economic Zone',
        sections: [{ id: 'eez-details', fields }],
      };

      setMapPopupContent(popup, e.lngLat, popupProps);
      popup.addTo(map);
    };

    const handleMouseMove = (e: mapboxgl.MapLayerMouseEvent) => {
      popup.setLngLat(e.lngLat);
    };

    const handleMouseLeave = () => {
      map.getCanvas().style.cursor = '';
      popup.remove();
    };

    // Attach to both the solid and dashed layers
    for (const layerId of [LAYER_ID, DASHED_LAYER_ID]) {
      map.on('mouseenter', layerId, handleMouseEnter);
      map.on('mousemove', layerId, handleMouseMove);
      map.on('mouseleave', layerId, handleMouseLeave);
    }
  } catch (error) {
    console.error('EEZ: Failed to load EEZ boundaries data:', error);
    throw error; // propagate so layerRegistry does not mark 'eez' initialized on failure
  }
}
